import React, { useEffect, useRef, useState } from 'react';
import { Chart, registerables } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import './Financials.css';

// Register all Chart.js components and the datalabels plugin
Chart.register(...registerables, ChartDataLabels);

const API_URL =
  process.env.NODE_ENV === 'production'
    ? 'https://boxcontents.yyzprep.ca:5000'
    : 'http://localhost:5000';

const UnmatchedChargesPanel = ({ charges, onAddRate, isResolvingCharges }) => {
  return (
    <div className="unmatched-panel">
      <h2>Unmatched Service Charges</h2>
      <p>The following charges need to be resolved before viewing financial data:</p>
      <div className="unmatched-charges-list">
        {charges.map(charge => (
          <div key={charge} className="unmatched-charge">
            <span>{charge}</span>
            <div className="charge-actions">
              <button
                onClick={() => onAddRate(charge, true)}
                className="zero-profit-btn"
                disabled={isResolvingCharges}
              >
                Mark as Zero Profit
              </button>
              <button
                onClick={() => onAddRate(charge, false)}
                className="add-rate-btn"
                disabled={isResolvingCharges}
              >
                Add Rate
              </button>
            </div>
          </div>
        ))}
      </div>
      <div className="unmatched-footer">
        <p className="modal-note">
          * Zero Profit charges are services where our cost equals the charge amount
        </p>
      </div>
    </div>
  );
};

// Move DrilldownModal outside of the Financials component
const DrilldownModal = ({ data, onClose }) => {
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'ascending'
  });
  const [searchTerm, setSearchTerm] = useState('');

  // Calculate total profit for percentage calculations
  const totalProfit = data?.totals.profit || 0;

  // Move useMemo before any conditionals and add search filter
  const sortedItems = React.useMemo(() => {
    if (!data) return [];
    
    let filteredItems = data.items;
    if (searchTerm) {
      filteredItems = data.items.filter(item => 
        item.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (!sortConfig.key) return filteredItems;

    return [...filteredItems].sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'ascending' ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'ascending' ? 1 : -1;
      }
      return 0;
    });
  }, [data, sortConfig, searchTerm]);

  if (!data) return null;

  // Calculate totals for filtered items
  const filteredTotals = {
    cost: sortedItems.reduce((sum, item) => sum + (item.total_cost || 0), 0),
    charge: sortedItems.reduce((sum, item) => sum + (item.total_charge || 0), 0),
    profit: sortedItems.reduce((sum, item) => sum + (item.profit || 0), 0)
  };

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return '↕';
    return sortConfig.direction === 'ascending' ? '↑' : '↓';
  };

  // Calculate gross margin for an item
  const calculateGrossMargin = (item) => {
    if (!item.total_charge) return 0;
    return ((item.total_charge - item.total_cost) / item.total_charge) * 100;
  };

  // Calculate total gross margin
  const totalGrossMargin = data.totals.charge ? 
    ((data.totals.charge - data.totals.cost) / data.totals.charge) * 100 : 0;

  return (
    <div className="drilldown-modal">
      <div className="drilldown-modal-content">
        <h2>{data.title}</h2>
        
        {/* Add search bar */}
        <div className="drilldown-search">
          <input
            type="text"
            placeholder="Search by description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          {searchTerm && (
            <button 
              className="clear-search"
              onClick={() => setSearchTerm('')}
            >
              ✕
            </button>
          )}
        </div>

        <div className="drilldown-table">
          <table>
            <thead>
              <tr>
                <th onClick={() => requestSort('description')}>
                  Description {getSortIndicator('description')}
                </th>
                <th onClick={() => requestSort('quantity')}>
                  Quantity {getSortIndicator('quantity')}
                </th>
                <th onClick={() => requestSort('cost_per_unit')}>
                  Cost per Unit {getSortIndicator('cost_per_unit')}
                </th>
                <th onClick={() => requestSort('total_cost')}>
                  Total Cost {getSortIndicator('total_cost')}
                </th>
                <th onClick={() => requestSort('total_charge')}>
                  Total Charges {getSortIndicator('total_charge')}
                </th>
                <th onClick={() => requestSort('profit')}>
                  Profit {getSortIndicator('profit')}
                </th>
                <th onClick={() => requestSort('profit_percentage')}>
                  % of Total Profit {getSortIndicator('profit_percentage')}
                </th>
                <th onClick={() => requestSort('gross_margin')}>
                  Gross Margin {getSortIndicator('gross_margin')}
                </th>
                <th onClick={() => requestSort('percentageChange')}>
                  Change from Last Month {getSortIndicator('percentageChange')}
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedItems.map((item, index) => (
                <tr key={index}>
                  <td>{item.description}</td>
                  <td>{item.quantity}</td>
                  <td>${item.cost_per_unit.toFixed(2)}</td>
                  <td>${item.total_cost.toFixed(2)}</td>
                  <td>${item.total_charge.toFixed(2)}</td>
                  <td style={{ color: item.profit >= 0 ? 'green' : 'red' }}>
                    ${item.profit.toFixed(2)}
                  </td>
                  <td style={{ color: item.profit >= 0 ? 'green' : 'red' }}>
                    {totalProfit ? ((item.profit / totalProfit) * 100).toFixed(1) : 0}%
                  </td>
                  <td style={{ color: calculateGrossMargin(item) >= 0 ? 'green' : 'red' }}>
                    {calculateGrossMargin(item).toFixed(1)}%
                  </td>
                  <td style={{ 
                    color: item.percentageChange === null ? 'inherit' : 
                           item.percentageChange >= 0 ? 'green' : 'red' 
                  }}
                  title={`Previous: Revenue $${item.prevTotalCharge || 0}, Cost $${item.prevTotalCost || 0}, Profit $${item.prevProfit || 0}`}>
                    {item.percentageChange === null ? 'N/A' : 
                     `${item.percentageChange >= 0 ? '↑' : '↓'} ${Math.abs(item.percentageChange).toFixed(1)}%`}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan="3">
                  <strong>
                    {searchTerm ? 'Filtered Totals:' : 'Totals:'}
                  </strong>
                </td>
                <td><strong>${filteredTotals.cost.toFixed(2)}</strong></td>
                <td><strong>${filteredTotals.charge.toFixed(2)}</strong></td>
                <td style={{ color: filteredTotals.profit >= 0 ? 'green' : 'red' }}>
                  <strong>${filteredTotals.profit.toFixed(2)}</strong>
                </td>
                <td>
                  <strong>
                    {totalProfit ? ((filteredTotals.profit / totalProfit) * 100).toFixed(1) : 0}%
                  </strong>
                </td>
                <td style={{ color: calculateGrossMargin(filteredTotals) >= 0 ? 'green' : 'red' }}>
                  <strong>
                    {(filteredTotals.charge ? 
                      ((filteredTotals.profit / filteredTotals.charge) * 100) : 0
                    ).toFixed(1)}%
                  </strong>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
        <div className="modal-buttons">
          <button type="button" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

const Financials = () => {
  const [financialData, setFinancialData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [unmatchedCharges, setUnmatchedCharges] = useState([]);
  const [chargesAcknowledged, setChargesAcknowledged] = useState(false);
  const [isResolvingCharges, setIsResolvingCharges] = useState(false);

  // Chart references
  const revenueChartRef = useRef(null);
  const payrollChartRef = useRef(null);
  const variableCostsChartRef = useRef(null);
  const netProfitChartRef = useRef(null);
  const revenueBreakdownChartRef = useRef(null);

  // Keep chart instances to properly destroy them before re-creating
  const chartInstances = useRef({
    revenue: null,
    payroll: null,
    variableCosts: null,
    netProfit: null,
    revenueBreakdown: null
  });

  // Add these state variables at the top of the Financials component
  const [showRateModal, setShowRateModal] = useState(false);
  const [pendingRateInfo, setPendingRateInfo] = useState(null);

  // Add this state variable at the top with other state declarations
  const [zeroProfitServices, setZeroProfitServices] = useState([]);

  // Add new state for drill-down modal
  const [drilldownData, setDrilldownData] = useState(null);
  const [showDrilldownModal, setShowDrilldownModal] = useState(false);

  // Add a new state to track the selected month
  const [selectedMonthIndex, setSelectedMonthIndex] = useState(-1); // -1 means latest month

  // Add a new state to track the selected month for drilldowns
  const [drilldownMonthIndex, setDrilldownMonthIndex] = useState(-1); // -1 means latest month

  // Fetch financial data
  const fetchFinancialData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/financials/revenue`);
      if (!response.ok) {
        throw new Error('Failed to fetch financial data');
      }
      const data = await response.json();
      setFinancialData(data);
    } catch (err) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Initial data load
  useEffect(() => {
    fetchFinancialData();
  }, []);

  // Add this effect to load zero-profit services
  useEffect(() => {
    const loadZeroProfitServices = async () => {
      try {
        const response = await fetch(`${API_URL}/api/zero-profit-services`);
        if (!response.ok) throw new Error('Failed to load zero-profit services');
        const services = await response.json();
        setZeroProfitServices(services);
      } catch (error) {
        console.error('Error loading zero-profit services:', error);
      }
    };

    loadZeroProfitServices();
  }, []);

  // Update the useEffect that processes financial data to filter out known zero-profit services
  useEffect(() => {
    if (!financialData || chargesAcknowledged) return;

    const allUnmatched = new Set();
    (financialData.monthlyTotals || []).forEach(month => {
      (month.unmatchedCharges || []).forEach(charge => {
        // Only add to unmatched if it's not a known zero-profit service
        if (!zeroProfitServices.includes(charge)) {
          allUnmatched.add(charge);
        }
      });
    });

    const unmatchedArr = [...allUnmatched];
    if (unmatchedArr.length > 0) {
      setUnmatchedCharges(unmatchedArr);
    } else {
      setChargesAcknowledged(true);
    }
  }, [financialData, chargesAcknowledged, zeroProfitServices]);

  // Create/destroy charts
  useEffect(() => {
    if (!financialData || !chargesAcknowledged) return;

    // Store current chart instances at the start of the effect
    const currentCharts = chartInstances.current;
    
    // Destroy existing charts
    Object.values(currentCharts).forEach(chart => chart && chart.destroy());

    const monthlyData = (financialData.monthlyTotals || []).sort(
      (a, b) => a.monthKey.localeCompare(b.monthKey)
    );

    if (!monthlyData.length) return;

    // Prepare chart labels
    const labels = monthlyData.map(({ monthKey }) => {
      const [year, monthNum] = monthKey.split('-');
      return `${new Date(year, +monthNum - 1).toLocaleString('default', { month: 'short' })} ${year}`;
    });

    // Chart datasets
    const revenueData = monthlyData.map(m => m.total_revenue || 0);
    const payrollData = monthlyData.map(m => m.payroll?.PAYROLL || 0);
    const variableCostData = monthlyData.map(m =>
      (m.items || []).reduce((sum, item) => {
        if (item.description === "Shipping Charge") {
          // For shipping charges, cost is total_charge / 1.08
          const totalCharge = item.total_charge || 0;
          const cost = totalCharge / 1.08;
          return sum + cost;
        } else if (item.description === "CC Admin Fee") {
          // For CC Admin Fee, cost equals total charge (zero profit)
          return sum + (item.total_charge || 0);
        }
        return sum + (item.total_cost || 0);
      }, 0)
    );

    // Sum up fixed costs
    const monthlyFixedCost = (financialData.fixedCosts || []).reduce(
      (sum, item) => sum + (parseFloat(item.COST) || 0),
      0
    );

    // Net profit
    const netProfitData = revenueData.map((rev, i) => {
      const monthItems = monthlyData[i].items || [];
      const variableCosts = monthItems.reduce((sum, item) => {
        if (item.description === "Shipping Charge") {
          const totalCharge = item.total_charge || 0;
          const cost = totalCharge / 1.08;
          return sum + cost;
        } else if (item.description === "CC Admin Fee") {
          return sum + (item.total_charge || 0); // Cost equals charge
        }
        return sum + (item.total_cost || 0);
      }, 0);
      
      const profit = rev - payrollData[i] - variableCosts - monthlyFixedCost;
      return isNaN(profit) ? 0 : profit;
    });

    const commonOptions = {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            callback: value => `$${(value / 1000).toFixed(1)}k`
          }
        }
      },
      plugins: {
        datalabels: {
          anchor: 'end',
          align: 'top',
          formatter: value => `$${(value / 1000).toFixed(1)}k`,
          font: { weight: 'bold' },
          display: context => Math.abs(context.dataset.data[context.dataIndex]) > 1000
        },
        legend: {
          display: false
        }
      }
    };

    const chartConfigs = {
      netProfit: {
        ref: netProfitChartRef.current,
        config: {
          type: 'bar',
          data: {
            labels,
            datasets: [
              {
                label: 'Net Profit',
                data: netProfitData,
                backgroundColor: netProfitData.map(val =>
                  val >= 0 ? 'rgba(76, 175, 80, 0.6)' : 'rgba(244, 67, 54, 0.6)'
                ),
                borderColor: netProfitData.map(val =>
                  val >= 0 ? 'rgba(76, 175, 80, 1)' : 'rgba(244, 67, 54, 1)'
                ),
                borderWidth: 1
              }
            ]
          },
          options: {
            ...commonOptions,
            plugins: {
              ...commonOptions.plugins,
              title: {
                display: true,
                text: 'Monthly Net Profit'
              }
            },
            onClick: (event, elements) => {
              if (elements.length > 0) {
                const index = elements[0].index;
                console.log(`Net Profit Chart Clicked: Index ${index}`);
                setSelectedMonthIndex(index);
                setDrilldownMonthIndex(index); // Update drilldown month index
                
                // Update revenue breakdown chart
                if (chartInstances.current.revenueBreakdown) {
                  const selectedMonth = monthlyData[index];
                  console.log(`Updating Revenue Breakdown Chart for Month: ${selectedMonth.monthKey}`);
                  updateRevenueBreakdownChart(selectedMonth, monthlyFixedCost, chartInstances.current.revenueBreakdown, monthlyData);
                }
              }
            }
          }
        }
      },
      revenue: {
        ref: revenueChartRef.current,
        config: {
          type: 'bar',
          data: {
            labels,
            datasets: [
              {
                label: 'Revenue',
                data: revenueData,
                backgroundColor: 'rgba(76, 175, 80, 0.6)',
                borderColor: 'rgba(76, 175, 80, 1)',
                borderWidth: 1
              }
            ]
          },
          options: commonOptions
        }
      },
      payroll: {
        ref: payrollChartRef.current,
        config: {
          type: 'bar',
          data: {
            labels,
            datasets: [
              {
                label: 'Payroll',
                data: payrollData,
                backgroundColor: 'rgba(33, 150, 243, 0.6)',
                borderColor: 'rgba(33, 150, 243, 1)',
                borderWidth: 1
              }
            ]
          },
          options: commonOptions
        }
      },
      variableCosts: {
        ref: variableCostsChartRef.current,
        config: {
          type: 'bar',
          data: {
            labels,
            datasets: [
              {
                label: 'Variable Costs',
                data: variableCostData,
                backgroundColor: 'rgba(244, 67, 54, 0.6)',
                borderColor: 'rgba(244, 67, 54, 1)',
                borderWidth: 1
              }
            ]
          },
          options: commonOptions
        }
      },
      revenueBreakdown: {
        ref: revenueBreakdownChartRef.current,
        config: {
          type: 'bar',
          data: {
            labels: ['Revenue', 'Payroll', 'Variable Costs', 'Fixed Costs', 'Net Profit'],
            datasets: [{
              data: (() => {
                // Use selected month or latest month
                const monthData = monthlyData[selectedMonthIndex >= 0 ? selectedMonthIndex : monthlyData.length - 1];
                
                const revenue = monthData.total_revenue || 0;
                const payroll = monthData.payroll?.PAYROLL || 0;
                const variableCosts = (monthData.items || []).reduce((sum, item) => {
                  if (item.description === "Shipping Charge") {
                    const totalCharge = item.total_charge || 0;
                    const cost = totalCharge / 1.08;
                    return sum + cost;
                  } else if (item.description === "CC Admin Fee") {
                    return sum + (item.total_charge || 0);
                  }
                  return sum + (item.total_cost || 0);
                }, 0);
                const fixedCosts = monthlyFixedCost;
                const netProfit = revenue - payroll - variableCosts - fixedCosts;

                return [
                  revenue,
                  -payroll,
                  -variableCosts,
                  -fixedCosts,
                  netProfit
                ];
              })(),
              backgroundColor: [
                'rgba(76, 175, 80, 0.6)',   // Revenue (green)
                'rgba(33, 150, 243, 0.6)',  // Payroll (blue)
                'rgba(244, 67, 54, 0.6)',   // Variable Costs (red)
                'rgba(156, 39, 176, 0.6)',  // Fixed Costs (purple)
                'rgba(76, 175, 80, 0.6)'    // Net Profit (green)
              ],
              borderColor: [
                'rgba(76, 175, 80, 1)',     // Revenue
                'rgba(33, 150, 243, 1)',    // Payroll
                'rgba(244, 67, 54, 1)',     // Variable Costs
                'rgba(156, 39, 176, 1)',    // Fixed Costs
                'rgba(76, 175, 80, 1)'      // Net Profit
              ],
              borderWidth: 1
            }]
          },
          options: {
            ...commonOptions,
            plugins: {
              ...commonOptions.plugins,
              legend: {
                display: false
              },
              title: {
                display: true,
                text: `Revenue Breakdown - ${monthlyData[selectedMonthIndex >= 0 ? selectedMonthIndex : monthlyData.length - 1].monthKey}`
              },
              tooltip: {
                callbacks: {
                  label: function(context) {
                    const value = context.raw;
                    const formattedValue = Math.abs(value).toLocaleString('en-US', {
                      style: 'currency',
                      currency: 'USD',
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0
                    });
                    if (context.dataIndex === 0) return `Revenue: ${formattedValue}`;
                    if (context.dataIndex === 4) return `Net Profit: ${formattedValue}`;
                    return `Cost: ${formattedValue}`;
                  }
                }
              }
            },
            scales: {
              y: {
                beginAtZero: true,
                ticks: {
                  callback: value => `$${(value / 1000).toFixed(1)}k`
                }
              }
            },
            onClick: (event, elements) => {
              if (!elements.length) return;
              const index = elements[0].index;
              console.log(`Revenue Breakdown Chart Clicked: Index ${index}`);

              // Update the drilldownMonthIndex to reflect the clicked month
              setDrilldownMonthIndex(index);

              // Use the updated drilldownMonthIndex to get the current month
              const currentMonthIndex = index;
              const currentMonth = monthlyData[currentMonthIndex];
              console.log(`Current Month for Drilldown: ${currentMonth.monthKey}`);
              const previousMonth = currentMonthIndex > 0 ? 
                monthlyData[currentMonthIndex - 1] : 
                monthlyData[monthlyData.length - 2];

              switch(index) {
                case 1: // Payroll
                  setDrilldownData({
                    title: `Payroll Breakdown - ${currentMonth.monthKey}`,
                    items: currentMonth.payroll?.details || [],
                    totals: {
                      cost: currentMonth.payroll?.PAYROLL || 0,
                      charge: 0,
                      profit: -(currentMonth.payroll?.PAYROLL || 0)
                    }
                  });
                  setShowDrilldownModal(true);
                  break;
                
                case 2: // Variable Costs
                  const currentItems = groupItemsByDescription(currentMonth.items);
                  const previousItems = previousMonth ? groupItemsByDescription(previousMonth.items) : {};

                  const items = Object.values(currentItems).map(group => {
                    const prevGroup = previousItems[group.description];
                    const percentageChange = prevGroup ? 
                      ((group.total_cost - prevGroup.total_cost) / Math.abs(prevGroup.total_cost)) * 100 : null;

                    return {
                      description: group.description,
                      quantity: group.quantity,
                      cost_per_unit: group.total_cost / group.quantity,
                      total_cost: group.total_cost,
                      total_charge: group.total_charge,
                      profit: group.total_charge - group.total_cost,
                      percentageChange
                    };
                  });

                  setDrilldownData({
                    title: `Variable Costs Breakdown - ${currentMonth.monthKey}`,
                    items,
                    totals: {
                      cost: items.reduce((sum, item) => sum + (item.total_cost || 0), 0),
                      charge: items.reduce((sum, item) => sum + (item.total_charge || 0), 0),
                      profit: items.reduce((sum, item) => sum + (item.profit || 0), 0)
                    }
                  });
                  setShowDrilldownModal(true);
                  break;
              }
            }
          }
        }
      }
    };

    Object.entries(chartConfigs).forEach(([key, { ref, config }]) => {
      if (ref) {
        chartInstances.current[key] = new Chart(ref.getContext('2d'), config);
      }
    });

    // Use the stored reference in the cleanup
    return () => {
      Object.values(currentCharts).forEach(chart => chart && chart.destroy());
    };
  }, [financialData, chargesAcknowledged]);

  // Add a new RateModal component
  const RateModal = ({ show, onClose, onSubmit, initialData }) => {
    const [rateInfo, setRateInfo] = useState({
      SERVICE_NAME: initialData?.serviceName || '',
      TIER_1_RATE: '',
      TIER_2_RATE: '',
      TIER_3_RATE: '',
      TIER_4_RATE: '',
      TYPE: '',
      UNIT: '',
      COST: initialData?.isZeroProfit ? 'MATCH_RATE' : ''
    });

    const handleSubmit = (e) => {
      e.preventDefault();
      onSubmit(rateInfo);
    };

    if (!show) return null;

    return (
      <div className="rate-modal">
        <div className="rate-modal-content">
          <h2>Add Rate Information</h2>
          <form onSubmit={handleSubmit}>
            <div>
              <label>Service Name:</label>
              <input
                type="text"
                value={rateInfo.SERVICE_NAME}
                readOnly
              />
            </div>
            <div>
              <label>Tier 1 Rate:</label>
              <input
                type="number"
                step="0.01"
                value={rateInfo.TIER_1_RATE}
                onChange={(e) => setRateInfo(prev => ({
                  ...prev,
                  TIER_1_RATE: e.target.value
                }))}
              />
            </div>
            <div>
              <label>Tier 2 Rate:</label>
              <input
                type="number"
                step="0.01"
                value={rateInfo.TIER_2_RATE}
                onChange={(e) => setRateInfo(prev => ({
                  ...prev,
                  TIER_2_RATE: e.target.value
                }))}
              />
            </div>
            <div>
              <label>Tier 3 Rate:</label>
              <input
                type="number"
                step="0.01"
                value={rateInfo.TIER_3_RATE}
                onChange={(e) => setRateInfo(prev => ({
                  ...prev,
                  TIER_3_RATE: e.target.value
                }))}
              />
            </div>
            <div>
              <label>Tier 4 Rate:</label>
              <input
                type="number"
                step="0.01"
                value={rateInfo.TIER_4_RATE}
                onChange={(e) => setRateInfo(prev => ({
                  ...prev,
                  TIER_4_RATE: e.target.value
                }))}
              />
            </div>
            {!initialData?.isZeroProfit && (
              <div>
                <label>Cost:</label>
                <input
                  type="number"
                  step="0.01"
                  value={rateInfo.COST}
                  onChange={(e) => setRateInfo(prev => ({
                    ...prev,
                    COST: e.target.value
                  }))}
                />
              </div>
            )}
            <div>
              <label>Type:</label>
              <input
                type="text"
                value={rateInfo.TYPE}
                onChange={(e) => setRateInfo(prev => ({
                  ...prev,
                  TYPE: e.target.value
                }))}
              />
            </div>
            <div>
              <label>Unit:</label>
              <input
                type="text"
                value={rateInfo.UNIT}
                onChange={(e) => setRateInfo(prev => ({
                  ...prev,
                  UNIT: e.target.value
                }))}
              />
            </div>
            <div className="modal-buttons">
              <button type="submit">Save</button>
              <button type="button" onClick={onClose}>Cancel</button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // Update the handleAddRate function
  const handleAddRate = async (serviceName, isZeroProfit) => {
    try {
      setIsResolvingCharges(true);

      if (isZeroProfit) {
        // Directly log zero-profit charge without showing modal
        const logResponse = await fetch(`${API_URL}/api/log-zero-profit-charge`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            serviceName,
            timestamp: new Date().toISOString()
          })
        });
        
        if (!logResponse.ok) throw new Error('Failed to log zero-profit charge');

        // Remove from unmatched charges immediately
        setUnmatchedCharges(prev => prev.filter(charge => charge !== serviceName));

        if (unmatchedCharges.length === 1) {
          setChargesAcknowledged(true);
          await fetchFinancialData();
        }
      } else {
        // Only show modal for non-zero-profit items
        setPendingRateInfo({ serviceName, isZeroProfit });
        setShowRateModal(true);
      }
    } catch (err) {
      console.error('Error handling rate:', err);
    } finally {
      setIsResolvingCharges(false);
    }
  };

  // Update the handleRateSubmit function
  const handleRateSubmit = async (rateInfo) => {
    try {
      setIsResolvingCharges(true);

      // Generate a random 6-digit integer for SERVICE_ID
      const randomServiceId = Math.floor(100000 + Math.random() * 900000);

      if (pendingRateInfo.isZeroProfit) {
        const logResponse = await fetch(`${API_URL}/api/log-zero-profit-charge`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            serviceName: pendingRateInfo.serviceName,
            timestamp: new Date().toISOString()
          })
        });
        if (!logResponse.ok) throw new Error('Failed to log zero-profit charge');
      }

      const response = await fetch(`${API_URL}/api/add-rate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...rateInfo,
          SERVICE_ID: randomServiceId // Use the random 6-digit integer
        })
      });
      
      if (!response.ok) throw new Error('Failed to add rate');

      setUnmatchedCharges(prev => prev.filter(charge => charge !== pendingRateInfo.serviceName));

      if (unmatchedCharges.length === 1) {
        setChargesAcknowledged(true);
        await fetchFinancialData();
      }

      setShowRateModal(false);
      setPendingRateInfo(null);
    } catch (err) {
      console.error('Error handling rate:', err);
    } finally {
      setIsResolvingCharges(false);
    }
  };

  // Update the updateRevenueBreakdownChart function to calculate and show percentage changes
  const updateRevenueBreakdownChart = (monthData, fixedCost, chart, allMonthlyData) => {
    // Find previous month's data
    const monthIndex = allMonthlyData.findIndex(m => m.monthKey === monthData.monthKey);
    const prevMonthData = monthIndex > 0 ? allMonthlyData[monthIndex - 1] : null;

    const revenue = monthData.total_revenue || 0;
    const payroll = monthData.payroll?.PAYROLL || 0;
    const variableCosts = (monthData.items || []).reduce((sum, item) => {
      if (item.description === "Shipping Charge") {
        const totalCharge = item.total_charge || 0;
        const cost = totalCharge / 1.08;
        return sum + cost;
      } else if (item.description === "CC Admin Fee") {
        return sum + (item.total_charge || 0);
      }
      return sum + (item.total_cost || 0);
    }, 0);
    const netProfit = revenue - payroll - variableCosts - fixedCost;

    // Calculate previous month's values
    let prevRevenue = 0, prevPayroll = 0, prevVariableCosts = 0, prevNetProfit = 0;
    if (prevMonthData) {
      prevRevenue = prevMonthData.total_revenue || 0;
      prevPayroll = prevMonthData.payroll?.PAYROLL || 0;
      prevVariableCosts = (prevMonthData.items || []).reduce((sum, item) => {
        if (item.description === "Shipping Charge") {
          const totalCharge = item.total_charge || 0;
          return sum + (totalCharge / 1.08);
        } else if (item.description === "CC Admin Fee") {
          return sum + (item.total_charge || 0);
        }
        return sum + (item.total_cost || 0);
      }, 0);
      prevNetProfit = prevRevenue - prevPayroll - prevVariableCosts - fixedCost;
    }

    // Calculate percentage changes
    const getPercentageChange = (current, previous) => {
      if (!previous) return null;
      return ((current - previous) / Math.abs(previous)) * 100;
    };

    const changes = [
      getPercentageChange(revenue, prevRevenue),
      getPercentageChange(payroll, prevPayroll),
      getPercentageChange(variableCosts, prevVariableCosts),
      null, // Fixed costs don't change
      getPercentageChange(netProfit, prevNetProfit)
    ];

    chart.data.datasets[0].data = [revenue, -payroll, -variableCosts, -fixedCost, netProfit];
    
    // Update the chart options to show percentage changes
    chart.options.plugins.datalabels = {
      formatter: (value, context) => {
        const change = changes[context.dataIndex];
        if (change === null) return '';
        const arrow = change >= 0 ? '↑' : '↓';
        const color = change >= 0 ? 'green' : 'red';
        return `${arrow} ${Math.abs(change).toFixed(1)}%`;
      },
      align: 'top',
      anchor: 'end',
      offset: 4,
      color: (context) => {
        const change = changes[context.dataIndex];
        if (change === null) return 'transparent';
        return change >= 0 ? 'green' : 'red';
      },
      font: {
        weight: 'bold',
        size: 14
      }
    };

    chart.options.plugins.title.text = `Revenue Breakdown - ${monthData.monthKey}`;

    // Update drilldown data if it's currently showing
    if (showDrilldownModal) {
      // Check which type of drilldown is showing and update accordingly
      if (drilldownData.title.includes('Payroll')) {
        setDrilldownData({
          title: `Payroll Breakdown - ${monthData.monthKey}`,
          items: monthData.payroll?.details || [],
          totals: {
            cost: monthData.payroll?.PAYROLL || 0,
            charge: 0,
            profit: -(monthData.payroll?.PAYROLL || 0)
          }
        });
      } else if (drilldownData.title.includes('Variable')) {
        const currentItems = groupItemsByDescription(monthData.items);
        const previousMonth = monthIndex > 0 ? allMonthlyData[monthIndex - 1] : null;
        const previousItems = previousMonth ? groupItemsByDescription(previousMonth.items) : {};

        const items = Object.values(currentItems).map(group => {
          const prevGroup = previousItems[group.description];
          const percentageChange = prevGroup ? 
            ((group.total_cost - prevGroup.total_cost) / Math.abs(prevGroup.total_cost)) * 100 : null;

          return {
            description: group.description,
            quantity: group.quantity,
            cost_per_unit: group.total_cost / group.quantity,
            total_cost: group.total_cost,
            total_charge: group.total_charge,
            profit: group.total_charge - group.total_cost,
            percentageChange
          };
        });

        setDrilldownData({
          title: `Variable Costs Breakdown - ${monthData.monthKey}`,
          items,
          totals: {
            cost: items.reduce((sum, item) => sum + (item.total_cost || 0), 0),
            charge: items.reduce((sum, item) => sum + (item.total_charge || 0), 0),
            profit: items.reduce((sum, item) => sum + (item.profit || 0), 0)
          }
        });
      }
    }

    chart.update();
  };

  // Add this helper function
  const groupItemsByDescription = (items) => {
    return items.reduce((acc, item) => {
      const key = item.description;
      if (!acc[key]) {
        acc[key] = {
          description: key,
          quantity: 0,
          total_cost: 0,
          total_charge: 0,
          items: []
        };
      }

      acc[key].items.push(item);
      acc[key].quantity += (item.quantity || 1);
      acc[key].total_charge += (item.total_charge || 0);

      // Handle special cases
      if (key === "Shipping Charge") {
        const totalCharge = item.total_charge || 0;
        acc[key].total_cost += totalCharge / 1.08;
      } else if (key === "CC Admin Fee") {
        acc[key].total_cost += (item.total_charge || 0);
      } else {
        acc[key].total_cost += (item.total_cost || 0);
      }

      return acc;
    }, {});
  };

  if (loading) {
    return <div className="financials-loading">Loading financial data...</div>;
  }
  if (error) {
    return <div className="financials-error">Error: {error}</div>;
  }
  if (!financialData) {
    return <div className="financials-error">No data available</div>;
  }

  const { statistics } = financialData;

  return (
    <div className="financials-container">
      <div className="financials-header">
        <h1>Financial Dashboard</h1>
      </div>

      {!chargesAcknowledged && unmatchedCharges.length > 0 && (
        <UnmatchedChargesPanel
          charges={unmatchedCharges}
          onAddRate={handleAddRate}
          isResolvingCharges={isResolvingCharges}
        />
      )}

      <RateModal
        show={showRateModal}
        onClose={() => {
          setShowRateModal(false);
          setPendingRateInfo(null);
        }}
        onSubmit={handleRateSubmit}
        initialData={pendingRateInfo}
      />

      {showDrilldownModal && (
        <DrilldownModal
          data={drilldownData}
          onClose={() => {
            setShowDrilldownModal(false);
            setDrilldownData(null);
          }}
        />
      )}

      {chargesAcknowledged && (
        <div className="charts-container">
          <div className="charts-row">
            <div className="chart-section half-width">
              <h2>Net Profit</h2>
              <div className="chart-wrapper">
                <canvas ref={netProfitChartRef} style={{ height: '300px' }} />
              </div>
            </div>

            <div className="chart-section half-width">
              <h2>Revenue Breakdown</h2>
              <div className="chart-wrapper">
                <canvas ref={revenueBreakdownChartRef} style={{ height: '300px' }} />
              </div>
            </div>
          </div>

          <div className="charts-row">
            <div className="chart-section third-width">
              <h2>Monthly Revenue</h2>
              <div className="chart-wrapper">
                <canvas ref={revenueChartRef} style={{ height: '300px' }} />
              </div>
            </div>

            <div className="chart-section third-width">
              <h2>Monthly Payroll
                <button 
                  className="refresh-button"
                  onClick={async () => {
                    try {
                      console.log('🔄 Starting payroll refresh process...');
                      console.time('Payroll Refresh');
                      
                      console.log('📡 Making request to /api/refresh-payroll...');
                      const response = await fetch(`${API_URL}/api/refresh-payroll`, {
                        method: 'POST',
                      });

                      if (!response.ok) {
                        const errorData = await response.json();
                        console.error('❌ Payroll refresh failed:', errorData);
                        throw new Error(errorData.error || 'Failed to refresh payroll data');
                      }

                      const result = await response.json();
                      console.log('✅ Payroll refresh successful:', result);
                      
                      console.log('🔄 Fetching updated financial data...');
                      await fetchFinancialData();
                      console.log('✅ Financial data updated successfully');
                      
                      console.timeEnd('Payroll Refresh');
                    } catch (error) {
                      console.error('❌ Error during payroll refresh:', error);
                    }
                  }}
                >
                  🔄 Refresh
                </button>
              </h2>
              <div className="chart-wrapper">
                <canvas ref={payrollChartRef} style={{ height: '300px' }} />
              </div>
            </div>

            <div className="chart-section third-width">
              <h2>Monthly Variable Costs</h2>
              <div className="chart-wrapper">
                <canvas ref={variableCostsChartRef} style={{ height: '300px' }} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Financials;
